import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Badge, RobbieMark } from '../../../components/ui';
import FixCheckCard from './FixCheckCard';
import ImprovementCard from './ImprovementCard';
import { AISuggestion } from './types';
import { ValidationStepState } from './useValidationStep';
import { ValidationEntry, otherAffectedNames } from './validationEntries';

/**
 * Validation detail pane (right side of the master–detail): the selected
 * entry's findings as actionable cards, its improvement ideas below, and —
 * for the "Entire BOM" entry — Robbie's overall read on top plus the list
 * of checks that passed at the bottom.
 */
interface ValidationDetailPaneProps {
  entry: ValidationEntry;
  state: ValidationStepState;
  aiSuggestion: AISuggestion | null;
  passedNames: string[];
}

const PassedSummary: React.FC<{ names: string[] }> = ({ names }) => {
  const [expanded, setExpanded] = useState(false);
  if (names.length === 0) return null;
  const preview = names.slice(0, 5);
  const rest = names.length - preview.length;
  return (
    <div className="flex items-start gap-[11px] rounded-[9px] border border-line bg-surface px-s5 py-[12px]">
      <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[7px] border border-success-border bg-success-bg">
        <CheckCircle2 className="h-3 w-3 text-success-ink" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-s3">
          <span className="text-row-title text-ink">
            {names.length} {names.length === 1 ? 'thing looks' : 'things look'} good
          </span>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-meta font-semibold text-action hover:text-action-deep"
          >
            {expanded ? 'Hide' : 'Show all'}
          </button>
        </div>
        {expanded ? (
          <ul className="m-0 mt-s2 flex list-none flex-col gap-[3px] p-0">
            {names.map((n) => (
              <li key={n} className="flex items-center gap-s2 text-body-sm text-ink-body">
                <CheckCircle2 className="h-[11px] w-[11px] shrink-0 text-success-ink" />
                {n}
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 mt-[3px] text-body-sm text-ink-muted">
            {preview.join(', ')}
            {rest > 0 ? ` and ${rest} more.` : '.'}
          </p>
        )}
      </div>
    </div>
  );
};

const RobbieRead: React.FC<{ suggestion: AISuggestion }> = ({ suggestion }) => {
  const [whyOpen, setWhyOpen] = useState(false);
  return (
    <div className="rounded-[9px] border border-robbie-border bg-ai-chip px-s5 py-s4">
      <div className="flex flex-wrap items-center gap-s3">
        <RobbieMark size="xs" state="insight" beacon aria-label="Robbie's read" />
        <span className="text-row-title text-ink">{suggestion.title}</span>
        {suggestion.confidence != null && (
          <Badge tone="robbie" mark={false} mono>
            {Math.round(suggestion.confidence * 100)}% confident
          </Badge>
        )}
        <button
          type="button"
          onClick={() => setWhyOpen((v) => !v)}
          className="ml-auto text-meta font-semibold text-robbie hover:text-robbie-ink"
        >
          {whyOpen ? 'Hide my reasoning' : 'My reasoning →'}
        </button>
      </div>
      <p className="m-0 mt-s2 text-body-sm text-ink-body">{suggestion.summary}</p>
      {whyOpen && (
        <div className="mt-s3 border-t border-robbie-border-soft pt-s3">
          <p className="m-0 text-body-sm text-ink-body">{suggestion.reasoning}</p>
          {suggestion.tradeoffs?.length > 0 && (
            <ul className="m-0 mt-s2 flex list-none flex-col gap-[3px] p-0">
              {suggestion.tradeoffs.map((t) => (
                <li key={t} className="flex items-start gap-s2 text-meta text-ink-secondary">
                  <span className="mt-[6px] h-[4px] w-[4px] shrink-0 rounded-round bg-ink-muted" />
                  {t}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export const ValidationDetailPane: React.FC<ValidationDetailPaneProps> = ({
  entry,
  state,
  aiSuggestion,
  passedNames,
}) => {
  const isBom = entry.kind === 'bom';
  const unresolved = entry.findings.filter((f) => state.findingStatus(f) === 'needs_review').length;
  const summaryBits = [
    entry.findings.length > 0
      ? `${entry.findings.length} ${entry.findings.length === 1 ? 'finding' : 'findings'}${
          unresolved === 0 ? ' — all handled' : ''
        }`
      : 'no findings',
    entry.improvements.length > 0 &&
      `${entry.improvements.length} ${entry.improvements.length === 1 ? 'improvement idea' : 'improvement ideas'}`,
  ].filter(Boolean);

  return (
    <div className="flex min-w-0 flex-col overflow-y-auto max-md:min-h-[62vh]">
      <div className="shrink-0 border-b border-line-divider-soft px-s6 py-[12px]">
        <div className="flex flex-wrap items-baseline gap-s3">
          <h3 className="m-0 text-card-header text-ink">{entry.name}</h3>
          {entry.quantity != null && <span className="text-meta text-ink-muted">Qty {entry.quantity}</span>}
          {entry.partNumber && <span className="font-mono text-[10.5px] text-ink-muted">{entry.partNumber}</span>}
          {entry.mpn && <span className="font-mono text-[10.5px] text-ink-muted">MPN {entry.mpn}</span>}
        </div>
        <p className="m-0 mt-[2px] text-meta text-ink-muted">
          {isBom ? 'BOM-wide checks · ' : ''}
          {summaryBits.join(' · ')}
        </p>
      </div>

      <div className="flex flex-col gap-s3 px-s6 py-s4">
        {isBom && aiSuggestion && <RobbieRead suggestion={aiSuggestion} />}

        {entry.findings.map((finding) => (
          <FixCheckCard
            key={finding.key}
            finding={finding}
            status={state.findingStatus(finding)}
            isBusy={state.isBusy}
            isComponentInBom={state.isComponentInBom}
            errorOf={state.errorOf}
            alsoAffects={otherAffectedNames(entry, finding)}
            onAddComponent={state.addComponent}
            onApplyValue={state.applyValue}
            onSkip={state.skip}
            readOnly={state.readOnly}
          />
        ))}

        {entry.improvements.length > 0 && (
          <div className="rounded-[9px] border border-robbie-border bg-ai-panel px-s4 py-s4">
            <div className="mb-s3 flex flex-wrap items-center gap-s3">
              <RobbieMark size="xs" aria-label="Robbie" />
              <span className="text-[13px] font-bold text-ink">
                {isBom ? 'Ways to improve your BOM' : `Ways to improve ${entry.name}`}
              </span>
              <span className="ml-auto text-[11px] text-ink-muted">Optional — your BOM works without them</span>
            </div>
            <div className="flex flex-col gap-s3">
              {entry.improvements.map((improvement) => (
                <ImprovementCard
                  key={improvement.key}
                  improvement={improvement}
                  status={state.improvementStatus(improvement)}
                  isBusy={state.isBusy}
                  errorOf={state.errorOf}
                  component={state.componentAt(improvement.componentIndex)}
                  onSwap={state.swapAlternative}
                  onKeep={state.skip}
                  onRevert={state.revert}
                  readOnly={state.readOnly}
                />
              ))}
            </div>
          </div>
        )}

        {entry.findings.length === 0 && entry.improvements.length === 0 && !isBom && (
          <div className="flex flex-col items-center gap-s2 rounded-[9px] border border-line bg-surface px-s5 py-[28px] text-center">
            <CheckCircle2 className="h-5 w-5 text-success-ink" />
            <p className="m-0 text-body-sm text-ink-secondary">
              Nothing to fix here — this component passed every check that names it.
            </p>
          </div>
        )}

        {isBom && <PassedSummary names={passedNames} />}
      </div>
    </div>
  );
};

export default ValidationDetailPane;
